import { useEffect, useState } from "react";

// Seconds-level "how long ago" - utils/helper's formatRelativeTime only has
// minute precision ("Just now" under a minute), which is useless for judging
// a camera that talks to the backend every few seconds. Pure/exported for
// testing.
export function describeAgo(timestamp, nowMs = Date.now()) {
  if (!timestamp) return "never";
  const seconds = Math.max(0, Math.round((nowMs - new Date(timestamp).getTime()) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

// When the camera should next take a scheduled photo: its last photo plus its
// own reported interval (a firmware constant the camera reports on every poll;
// the run's configured interval is only what the dashboard asked for, and a
// standalone camera ignores it), falling back to the configured one. Uses the
// server's receive time, not the device clock, so a camera with a wrong clock
// can't skew it. Pure/exported for testing.
export function describeNextCapture(status, nowMs = Date.now()) {
  if (!status?.run) return "No active run";
  const interval = status.reported_interval_seconds || status.configured_interval_seconds;
  if (!interval) return "—";
  const received = status.last_image?.server_received_at;
  if (!received) return "Waiting for first photo";
  const remainingSeconds = Math.round(
    (new Date(received).getTime() + interval * 1000 - nowMs) / 1000
  );
  if (remainingSeconds <= 0) return status.online ? "Due now" : "Overdue";
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = String(remainingSeconds % 60).padStart(2, "0");
  return `in ${minutes}:${seconds}`;
}

const formatBytes = (bytes) => (bytes ? `${(bytes / 1024).toFixed(1)} KiB` : "—");

function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="truncate text-sm font-semibold text-slate-800">{children}</dd>
    </div>
  );
}

// `nowMs` is only for tests; in the app the card ticks once a second so the
// "last contact" and next-capture countdown stay live between status polls.
export default function CameraDeviceStatusCard({ deviceName, mac, status, error, loading, nowMs }) {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    if (nowMs !== undefined) return undefined;
    const timer = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [nowMs]);
  const now = nowMs ?? tick;

  let body;
  if (!mac) {
    body = <p className="text-sm text-slate-500">Select a ground-truth camera to see its status.</p>;
  } else if (!status && error) {
    body = (
      <p role="alert" className="text-sm text-red-700">
        Couldn't load camera status: {error}
      </p>
    );
  } else if (!status) {
    body = <p className="text-sm text-slate-500">{loading ? "Loading camera status…" : "No status yet."}</p>;
  } else if (!status.standalone) {
    body = (
      <p className="text-sm text-slate-500">
        This device isn't a standalone (HTTP) camera, so live status isn't available here.
      </p>
    );
  } else {
    const last = status.last_image;
    body = (
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-6">
        <Field label="Last contact">{describeAgo(status.last_status_at, now)}</Field>
        <Field label="Firmware">{status.last_reported_fw || "—"}</Field>
        <Field label="Active run">{status.run || "None"}</Field>
        <Field label="Last photo">
          {last ? (
            <span title={`${last.width}×${last.height} · ${formatBytes(last.jpeg_bytes)} · #${last.capture_id}`}>
              {describeAgo(last.server_received_at, now)} · {last.width}×{last.height} ·{" "}
              {formatBytes(last.jpeg_bytes)}
            </span>
          ) : (
            "None yet"
          )}
        </Field>
        <Field label="Next photo">{describeNextCapture(status, now)}</Field>
        <Field label="Capture request">{status.test_capture_pending ? "Pending" : "None"}</Field>
      </dl>
    );
  }

  const showState = mac && status && status.standalone;
  const online = Boolean(status?.online);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-4"
      data-testid="camera-device-status-card"
      aria-label="Ground-truth camera status"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Ground-truth camera
          </p>
          <p className="truncate text-sm font-semibold text-slate-800">{deviceName || mac || "—"}</p>
        </div>
        {showState && (
          <span
            className={`inline-flex shrink-0 items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${
              online ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
            }`}
          >
            <span
              className={`h-2.5 w-2.5 rounded-full ${online ? "bg-emerald-500" : "bg-red-500"}`}
              aria-hidden="true"
            />
            {online ? "Online" : "Offline"}
          </span>
        )}
      </div>
      {body}
    </section>
  );
}
